import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Edit, Trash2, CalendarIcon, Pencil, User, ChevronDown } from 'lucide-react';
import { Tournament, Team, Player, Match } from '@/types/cricket';
import { toast } from '@/hooks/use-toast';
import ColorPicker from '@/components/ui/ColorPicker';
import Cropper from 'react-easy-crop';
import { Slider } from '@/components/ui/slider';
import { DialogFooter } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';

interface EntityActionsProps {
  entity: Tournament | Team | Player | Match;
  entityType: 'tournament' | 'team' | 'player' | 'match';
  onEdit: (entity: any) => void;
  onDelete: (id: string) => void;
  tournaments?: Tournament[];
  teams?: Team[];
  players?: Player[];
  triggerId?: string;
  showDialog?: boolean;
}

export const EntityActions: React.FC<EntityActionsProps> = ({
  entity,
  entityType,
  onEdit,
  onDelete,
  tournaments = [],
  teams = [],
  players = [],
  triggerId,
  showDialog = true
}) => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editData, setEditData] = useState<any>(entity);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(editData.logoUrl || null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [isCropDialogOpen, setIsCropDialogOpen] = useState(false);
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [bioOpen, setBioOpen] = useState(false);
  const [perfOpen, setPerfOpen] = useState(false);

  // Sync editData with entity prop
  useEffect(() => {
    setEditData(entity);
  }, [entity]);

  const handleEdit = () => {
    onEdit(editData);
    setIsEditOpen(false);
    const displayEntityType = entityType ? entityType.charAt(0).toUpperCase() + entityType.slice(1) : 'Entity';
    toast({
      title: "Updated",
      description: `${displayEntityType} updated successfully`,
    });
  };

  const handleDelete = () => {
    if (typeof onDelete === 'function') {
      onDelete(entity.id);
      const displayEntityType = entityType ? entityType.charAt(0).toUpperCase() + entityType.slice(1) : 'Entity';
      toast({
        title: "Deleted",
        description: `${displayEntityType} deleted successfully`,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Delete handler not implemented",
        description: "No delete function was provided to EntityActions.",
        variant: "destructive",
      });
    }
  };

  // Get players for specific teams
  const getTeamPlayers = (teamId: string) => {
    return players.filter(p => Array.isArray(p.teamIds) && p.teamIds.includes(teamId));
  };

  // Check if match is upcoming for conditional editing
  const isUpcomingMatch = entityType === 'match' && (entity as Match).status === 'upcoming';

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setLogoFile(file);
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImageSrc(reader.result as string);
        setIsCropDialogOpen(true);
      }, false);
      reader.readAsDataURL(file);
    }
  };

  const onCropComplete = (croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const getCroppedImage = async (imageSrc: string, pixelCrop: any, rotation = 0) => {
    // ... copy getCroppedImage logic from NewTeamPage ...
  };

  const handleCropConfirm = async () => {
    if (imageSrc && croppedAreaPixels) {
      const croppedFile = await getCroppedImage(imageSrc, croppedAreaPixels, rotation);
      if (croppedFile) {
        if (logoPreviewUrl) { URL.revokeObjectURL(logoPreviewUrl); }
        setLogoFile(croppedFile);
        const newPreviewUrl = URL.createObjectURL(croppedFile);
        setLogoPreviewUrl(newPreviewUrl);
        setEditData({ ...editData, logoFile: croppedFile });
      }
    }
    setIsCropDialogOpen(false);
  };

  const handleCropCancel = () => {
    setLogoFile(null);
    setImageSrc(null);
    setLogoPreviewUrl(editData.logoUrl || null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
    setIsCropDialogOpen(false);
  };

  const renderEditForm = () => {
    switch (entityType) {
      case 'tournament':
        return (
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={editData.name || ''}
                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                placeholder="Summer Cricket League 2023"
              />
              <div className="text-xs text-muted-foreground mt-1">Enter the official name of your tournament.</div>
            </div>
            <div>
              <Label>Location</Label>
              <Input
                value={editData.location || ''}
                onChange={(e) => setEditData({ ...editData, location: e.target.value })}
                placeholder="London, UK"
              />
              <div className="text-xs text-muted-foreground mt-1">Where will the tournament be held?</div>
            </div>
            <div>
              <Label>Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full pl-3 text-left font-normal',
                      !editData.startDate && 'text-muted-foreground'
                    )}
                  >
                    {editData.startDate ? (
                      format(new Date(editData.startDate), 'PPP')
                    ) : (
                      <span>Pick a date</span>
                    )}
                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 pointer-events-auto" align="start">
                  <Calendar
                    mode="single"
                    selected={editData.startDate ? new Date(editData.startDate) : undefined}
                    onSelect={(date) => setEditData({ ...editData, startDate: date ? date.toISOString() : '' })}
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
              <div className="text-xs text-muted-foreground mt-1">When will the tournament start?</div>
            </div>
            <div>
              <Label>Tournament Logo</Label>
              <div className="flex items-center space-x-4">
                <input
                  type="file"
                  id="tournament-logo-upload"
                  style={{ display: 'none' }}
                  accept="image/*"
                  onChange={handleLogoChange}
                />
                {!logoPreviewUrl ? (
                  <div
                    className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center cursor-pointer overflow-hidden relative group"
                    onClick={() => document.getElementById('tournament-logo-upload')?.click()}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="w-12 h-12 text-gray-500"
                    >
                      <path
                        fillRule="evenodd"
                        d="M1.5 6a2.25 2.25 0 0 1 2.25-2.25h16.5A2.25 2.25 0 0 1 22.5 6v12a2.25 2.25 0 0 1-2.25 2.25H3.75A2.25 2.25 0 0 1 1.5 18V6ZM3 16.06V18a.75.75 0 0 0 .75.75h16.5A.75.75 0 0 0 21 18v-1.94l-2.69-2.69a1.5 1.5 0 0 0-2.12 0L9.47 17.13a3 3 0 0 1-4.24 0L3 16.06ZM14.25 9a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Pencil className="h-6 w-6 text-white" />
                    </div>
                  </div>
                ) : (
                  <div
                    className="w-24 h-24 rounded-full overflow-hidden relative group cursor-pointer"
                    onClick={() => setIsCropDialogOpen(true)}
                  >
                    <img
                      key={logoPreviewUrl}
                      src={logoPreviewUrl}
                      alt="Tournament Logo Preview"
                      className="w-full h-full object-cover rounded-full"
                      onError={(e) => {
                        e.currentTarget.src = 'https://via.placeholder.com/96';
                      }}
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Pencil className="h-6 w-6 text-white" />
                    </div>
                  </div>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('tournament-logo-upload')?.click()}
                >
                  Choose File
                </Button>
              </div>
              <div className="text-xs text-muted-foreground mt-1">Upload an official logo for your tournament.</div>
              {imageSrc && isCropDialogOpen && (
                <Dialog open={isCropDialogOpen} onOpenChange={setIsCropDialogOpen}>
                  <DialogContent className="sm:max-w-[425px] h-[500px] flex flex-col">
                    <DialogHeader>
                      <DialogTitle>Crop Tournament Logo</DialogTitle>
                      <DialogDescription>
                        Adjust the crop, zoom, and rotation of your logo.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="relative w-full h-64 bg-gray-100 flex-grow">
                      <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        rotation={rotation}
                        aspect={1}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onRotationChange={setRotation}
                        onCropComplete={onCropComplete}
                        cropShape="round"
                      />
                    </div>
                    <div className="space-y-4 pt-4">
                      <div className="flex items-center space-x-2">
                        <Label className="w-16">Zoom</Label>
                        <Slider
                          min={1}
                          max={3}
                          step={0.1}
                          value={[zoom]}
                          onValueChange={([val]) => setZoom(val)}
                          className="flex-grow"
                        />
                      </div>
                      <div className="flex items-center space-x-2">
                        <Label className="w-16">Rotation</Label>
                        <Slider
                          min={0}
                          max={360}
                          step={1}
                          value={[rotation]}
                          onValueChange={([val]) => setRotation(val)}
                          className="flex-grow"
                        />
                      </div>
                    </div>
                    <DialogFooter className="flex justify-end gap-2 pt-4">
                      <Button variant="outline" onClick={handleCropCancel}>
                        Cancel
                      </Button>
                      <Button onClick={handleCropConfirm}>
                        OK
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>
        );
      
      case 'team':
        return (
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input 
                value={editData.name || ''} 
                onChange={(e) => setEditData({...editData, name: e.target.value})}
              />
            </div>
            <div>
              <Label>Short Code</Label>
              <Input 
                value={editData.shortCode || ''} 
                maxLength={3}
                onChange={(e) => setEditData({...editData, shortCode: e.target.value.slice(0,3).toUpperCase()})}
              />
            </div>
            <div className="flex items-center justify-between w-full">
              <div className="space-y-2 mb-4">
                <Label htmlFor="logo">Team Logo</Label>
                <Input 
                  id="logo" 
                  type="file" 
                  accept="image/*" 
                  onChange={handleLogoChange}
                  className="w-fit"
                />
              </div>
              {!logoPreviewUrl ? (
                <div 
                  className="flex items-center justify-center w-24 h-24 rounded-full bg-gray-200 text-gray-500 cursor-pointer border border-gray-300"
                  onClick={() => document.getElementById('logo')?.click()}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L20 16m-2-6a2 2 0 100-4 2 2 0 000 4zM6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              ) : (
                <div className="relative flex items-center group">
                  <img key={logoPreviewUrl} src={logoPreviewUrl} alt="Team Logo Preview" className="w-24 h-24 rounded-full object-cover border border-gray-200" />
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 cursor-pointer" onClick={() => setIsCropDialogOpen(true)}>
                    <Pencil className="h-8 w-8 text-white" />
                  </div>
                </div>
              )}
            </div>
            {imageSrc && isCropDialogOpen && (
              <Dialog open={isCropDialogOpen} onOpenChange={setIsCropDialogOpen}>
                <DialogContent className="sm:max-w-[425px]">
                  <DialogHeader>
                    <DialogTitle>Crop Team Logo</DialogTitle>
                  </DialogHeader>
                  <div className="relative w-full h-80 bg-gray-100 overflow-hidden flex items-center justify-center">
                    <Cropper
                      image={imageSrc}
                      crop={crop}
                      zoom={zoom}
                      rotation={rotation}
                      aspect={1}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onRotationChange={setRotation}
                      onCropComplete={onCropComplete}
                      cropShape="round"
                      showGrid={false}
                      restrictPosition={false}
                    />
                  </div>
                  <div className="space-y-4 py-4">
                    <div className="flex items-center gap-2">
                      <Label>Zoom:</Label>
                      <Slider min={1} max={3} step={0.1} value={[zoom]} onValueChange={([val]) => setZoom(val)} className="flex-grow" />
                    </div>
                    <div className="flex items-center gap-2">
                      <Label>Rotation:</Label>
                      <Slider min={0} max={360} step={1} value={[rotation]} onValueChange={([val]) => setRotation(val)} className="flex-grow" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={handleCropCancel}>Cancel</Button>
                    <Button onClick={handleCropConfirm}>OK</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
            <div>
              <Label>Tournament</Label>
              <Select 
                value={editData.tournamentId || ''} 
                onValueChange={(value) => setEditData({...editData, tournamentId: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select tournament" />
                </SelectTrigger>
                <SelectContent>
                  {tournaments.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Team Color</Label>
              <ColorPicker value={editData.color || '#10b981'} onChange={(color) => setEditData({...editData, color})} />
            </div>
          </div>
        );
      
      case 'player':
        return (
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={editData.name || ''}
                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                placeholder="Player Name"
              />
            </div>
            <div>
              <Label>Profile Picture</Label>
              <input
                type="file"
                id={`player-profile-upload-${editData.id}`}
                style={{ display: 'none' }}
                accept="image/*"
                onChange={handleLogoChange}
              />
              {!logoPreviewUrl ? (
                <div
                  className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center cursor-pointer overflow-hidden relative group"
                  onClick={() => document.getElementById(`player-profile-upload-${editData.id}`)?.click()}
                >
                  <User size={32} className="text-gray-500" />
                </div>
              ) : (
                <div
                  className="w-16 h-16 rounded-full overflow-hidden relative group cursor-pointer"
                  onClick={() => setIsCropDialogOpen(true)}
                >
                  <img
                    key={logoPreviewUrl}
                    src={logoPreviewUrl}
                    alt="Profile Preview"
                    className="w-full h-full object-cover rounded-full"
                    onError={(e) => {
                      e.currentTarget.src = 'https://via.placeholder.com/64';
                    }}
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Pencil className="h-5 w-5 text-white" />
                  </div>
                </div>
              )}
              {imageSrc && isCropDialogOpen && (
                <Dialog open={isCropDialogOpen} onOpenChange={setIsCropDialogOpen}>
                  <DialogContent className="sm:max-w-[425px] h-[400px] flex flex-col">
                    <DialogHeader>
                      <DialogTitle>Crop Profile Picture</DialogTitle>
                      <DialogDescription>Adjust the crop, zoom, and rotation of your profile picture.</DialogDescription>
                    </DialogHeader>
                    <div className="relative w-full h-48 bg-gray-100 flex-grow">
                      <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        rotation={rotation}
                        aspect={1}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onRotationChange={setRotation}
                        onCropComplete={onCropComplete}
                        cropShape="round"
                      />
                    </div>
                    <div className="space-y-4 pt-4">
                      <div className="flex items-center space-x-2">
                        <Label className="w-16">Zoom</Label>
                        <Slider min={1} max={3} step={0.1} value={[zoom]} onValueChange={([val]) => setZoom(val)} className="flex-grow" />
                      </div>
                      <div className="flex items-center space-x-2">
                        <Label className="w-16">Rotation</Label>
                        <Slider min={0} max={360} step={1} value={[rotation]} onValueChange={([val]) => setRotation(val)} className="flex-grow" />
                      </div>
                    </div>
                    <DialogFooter className="flex justify-end gap-2 pt-4">
                      <Button variant="outline" onClick={handleCropCancel}>Cancel</Button>
                      <Button onClick={handleCropConfirm}>OK</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </div>
            <div>
              <Label>Role</Label>
              <RadioGroup
                value={editData.role || 'batsman'}
                onValueChange={(value) => setEditData({ ...editData, role: value })}
                className="grid grid-cols-2 gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="batsman" id="role-batsman" />
                  <Label htmlFor="role-batsman">Batsman</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="bowler" id="role-bowler" />
                  <Label htmlFor="role-bowler">Bowler</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="all-rounder" id="role-all-rounder" />
                  <Label htmlFor="role-all-rounder">All-Rounder</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="wicket-keeper" id="role-wicket-keeper" />
                  <Label htmlFor="role-wicket-keeper">Wicket-Keeper</Label>
                </div>
              </RadioGroup>
            </div>
            <div>
              <Label>Team</Label>
              <Select
                value={editData.teamIds && editData.teamIds.length > 0 ? editData.teamIds[0] : ''}
                onValueChange={(value) => setEditData({ ...editData, teamIds: [value] })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select team" />
                </SelectTrigger>
                <SelectContent>
                  {teams.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Batting fields */}
            {(editData.role === 'batsman' || editData.role === 'all-rounder') && (
              <div className="space-y-2 border-t pt-4">
                <Label>Batting Style (optional)</Label>
                <Select value={editData.battingStyle || ''} onValueChange={v => setEditData({ ...editData, battingStyle: v })}>
                  <SelectTrigger><SelectValue placeholder="Select batting style" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="left-handed">Left-handed</SelectItem>
                    <SelectItem value="right-handed">Right-handed</SelectItem>
                  </SelectContent>
                </Select>
                <Label>Batting Order (optional)</Label>
                <Select value={editData.battingOrder || ''} onValueChange={v => setEditData({ ...editData, battingOrder: v })}>
                  <SelectTrigger><SelectValue placeholder="Select batting order" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="opener">Opener</SelectItem>
                    <SelectItem value="top-order">Top Order</SelectItem>
                    <SelectItem value="middle-order">Middle Order</SelectItem>
                    <SelectItem value="lower-order">Lower Order</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {/* Bowling fields */}
            {(editData.role === 'bowler' || editData.role === 'all-rounder') && (
              <div className="space-y-2 border-t pt-4">
                <Label>Bowling Style (optional)</Label>
                <Select value={editData.bowlingStyle || ''} onValueChange={v => setEditData({ ...editData, bowlingStyle: v })}>
                  <SelectTrigger><SelectValue placeholder="Select bowling style" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fast">Fast</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="spin">Spin</SelectItem>
                  </SelectContent>
                </Select>
                <Label>Bowling Arm (optional)</Label>
                <Select value={editData.bowlingArm || ''} onValueChange={v => setEditData({ ...editData, bowlingArm: v })}>
                  <SelectTrigger><SelectValue placeholder="Select bowling arm" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="left-arm">Left Arm</SelectItem>
                    <SelectItem value="right-arm">Right Arm</SelectItem>
                  </SelectContent>
                </Select>
                <Label>Bowling Type (optional)</Label>
                <Select value={editData.bowlingType || ''} onValueChange={v => setEditData({ ...editData, bowlingType: v })}>
                  <SelectTrigger><SelectValue placeholder="Select bowling type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pace">Pace</SelectItem>
                    <SelectItem value="off-spin">Off Spin</SelectItem>
                    <SelectItem value="leg-spin">Leg Spin</SelectItem>
                    <SelectItem value="left-arm-orthodox">Left Arm Orthodox</SelectItem>
                    <SelectItem value="left-arm-unorthodox">Left Arm Unorthodox</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {/* Keeping fields */}
            {editData.role === 'wicket-keeper' && (
              <div className="space-y-2 border-t pt-4">
                <Label>Keeping Style (optional)</Label>
                <Select value={editData.keepingStyle || ''} onValueChange={v => setEditData({ ...editData, keepingStyle: v })}>
                  <SelectTrigger><SelectValue placeholder="Select keeping style" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="conventional">Conventional</SelectItem>
                    <SelectItem value="aggressive">Aggressive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {/* Fielding fields */}
            <div className="space-y-2 border-t pt-4">
              <Label>Fielding Position (optional)</Label>
              <Input value={editData.fieldingPosition || ''} onChange={e => setEditData({ ...editData, fieldingPosition: e.target.value })} placeholder="Enter preferred fielding position" />
              <Label>Fielding Style (optional)</Label>
              <Select value={editData.fieldingStyle || ''} onValueChange={v => setEditData({ ...editData, fieldingStyle: v })}>
                <SelectTrigger><SelectValue placeholder="Select fielding style" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="aggressive">Aggressive</SelectItem>
                  <SelectItem value="balanced">Balanced</SelectItem>
                  <SelectItem value="defensive">Defensive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Bio Data (Optional) Collapsible */}
            <Collapsible open={bioOpen} onOpenChange={setBioOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" className="w-full flex items-center justify-between text-left font-semibold border-t pt-4">
                  <span>Bio Data (Optional)</span>
                  <ChevronDown className={`transition-transform ${bioOpen ? 'rotate-180' : ''}`} />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="space-y-2 pt-2">
                  <Label>Date of Birth (optional)</Label>
                  <Input type="date" value={editData.dateOfBirth || ''} onChange={e => setEditData({ ...editData, dateOfBirth: e.target.value })} />
                  <Label>Place of Birth (optional)</Label>
                  <Input value={editData.placeOfBirth || ''} onChange={e => setEditData({ ...editData, placeOfBirth: e.target.value })} placeholder="e.g., Sydney" />
                  <Label>Nationality (optional)</Label>
                  <Input value={editData.nationality || ''} onChange={e => setEditData({ ...editData, nationality: e.target.value })} placeholder="e.g., Australian" />
                  <Label>Height (cm, optional)</Label>
                  <Input type="number" value={editData.heightCm || ''} onChange={e => setEditData({ ...editData, heightCm: e.target.value })} placeholder="e.g., 180" />
                  <Label>Weight (kg, optional)</Label>
                  <Input type="number" value={editData.weightKg || ''} onChange={e => setEditData({ ...editData, weightKg: e.target.value })} placeholder="e.g., 75" />
                  <Label>Jersey Number (optional)</Label>
                  <Input type="number" value={editData.jerseyNumber || ''} onChange={e => setEditData({ ...editData, jerseyNumber: e.target.value })} placeholder="e.g., 10" />
                  <Label>Nickname (optional)</Label>
                  <Input value={editData.nickname || ''} onChange={e => setEditData({ ...editData, nickname: e.target.value })} placeholder="e.g., Punter" />
                  <Label>Gender (optional)</Label>
                  <Select value={editData.gender || ''} onValueChange={v => setEditData({ ...editData, gender: v })}>
                    <SelectTrigger><SelectValue placeholder="Select Gender" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CollapsibleContent>
            </Collapsible>
            {/* Optional Cricket Performance & Health Data Collapsible */}
            <Collapsible open={perfOpen} onOpenChange={setPerfOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" className="w-full flex items-center justify-between text-left font-semibold border-t pt-4">
                  <span>Optional Cricket Performance & Health Data</span>
                  <ChevronDown className={`transition-transform ${perfOpen ? 'rotate-180' : ''}`} />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="space-y-2 pt-2">
                  <Label>Injury Status (optional)</Label>
                  <Select value={editData.injuryStatus || ''} onValueChange={v => setEditData({ ...editData, injuryStatus: v })}>
                    <SelectTrigger><SelectValue placeholder="Select injury status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fit">Fit</SelectItem>
                      <SelectItem value="injured">Injured</SelectItem>
                      <SelectItem value="recovering">Recovering</SelectItem>
                    </SelectContent>
                  </Select>
                  <Label>Match Fitness (%) (optional)</Label>
                  <Input type="number" value={editData.matchFitness || ''} onChange={e => setEditData({ ...editData, matchFitness: e.target.value })} placeholder="e.g., 95" min={0} max={100} />
                  <Label>Recent Form (optional)</Label>
                  <Input value={editData.recentForm || ''} onChange={e => setEditData({ ...editData, recentForm: e.target.value })} placeholder="e.g., Good, Average, Poor" />
                  <Label>Career Runs (optional)</Label>
                  <Input type="number" value={editData.careerRuns || ''} onChange={e => setEditData({ ...editData, careerRuns: e.target.value })} placeholder="e.g., 5000" />
                  <Label>Matches Played (optional)</Label>
                  <Input type="number" value={editData.careerMatchesPlayed || ''} onChange={e => setEditData({ ...editData, careerMatchesPlayed: e.target.value })} placeholder="e.g., 100" />
                  <Label>Innings Played (optional)</Label>
                  <Input type="number" value={editData.careerInningsPlayed || ''} onChange={e => setEditData({ ...editData, careerInningsPlayed: e.target.value })} placeholder="e.g., 90" />
                  <Label>Strike Rate (optional)</Label>
                  <Input type="number" step="0.01" value={editData.careerStrikeRate || ''} onChange={e => setEditData({ ...editData, careerStrikeRate: e.target.value })} placeholder="e.g., 120.50" />
                  <Label>Average (optional)</Label>
                  <Input type="number" step="0.01" value={editData.careerAverage || ''} onChange={e => setEditData({ ...editData, careerAverage: e.target.value })} placeholder="e.g., 35.75" />
                  <Label>Highest Score (optional)</Label>
                  <Input type="number" value={editData.highestScore || ''} onChange={e => setEditData({ ...editData, highestScore: e.target.value })} placeholder="e.g., 150" />
                  <Label>Not Outs (optional)</Label>
                  <Input type="number" value={editData.notOuts || ''} onChange={e => setEditData({ ...editData, notOuts: e.target.value })} placeholder="e.g., 15" />
                  <Label>Fours (optional)</Label>
                  <Input type="number" value={editData.fours || ''} onChange={e => setEditData({ ...editData, fours: e.target.value })} placeholder="e.g., 450" />
                  <Label>Sixes (optional)</Label>
                  <Input type="number" value={editData.sixes || ''} onChange={e => setEditData({ ...editData, sixes: e.target.value })} placeholder="e.g., 100" />
                  <Label>Career Wickets (optional)</Label>
                  <Input type="number" value={editData.careerWickets || ''} onChange={e => setEditData({ ...editData, careerWickets: e.target.value })} placeholder="e.g., 200" />
                  <Label>Career Overs Bowled (optional)</Label>
                  <Input type="number" step="0.1" value={editData.careerOversBowled || ''} onChange={e => setEditData({ ...editData, careerOversBowled: e.target.value })} placeholder="e.g., 1500.3" />
                  <Label>Economy Rate (optional)</Label>
                  <Input type="number" step="0.01" value={editData.careerEconomyRate || ''} onChange={e => setEditData({ ...editData, careerEconomyRate: e.target.value })} placeholder="e.g., 5.25" />
                  <Label>Bowling Average (optional)</Label>
                  <Input type="number" step="0.01" value={editData.careerBowlingAverage || ''} onChange={e => setEditData({ ...editData, careerBowlingAverage: e.target.value })} placeholder="e.g., 25.50" />
                  <Label>Best Bowling Figures (optional)</Label>
                  <Input value={editData.bestBowlingFigures || ''} onChange={e => setEditData({ ...editData, bestBowlingFigures: e.target.value })} placeholder="e.g., 5/25" />
                  <Label>Maiden Overs (optional)</Label>
                  <Input type="number" value={editData.maidenOvers || ''} onChange={e => setEditData({ ...editData, maidenOvers: e.target.value })} placeholder="e.g., 10" />
                  <Label>Catches (optional)</Label>
                  <Input type="number" value={editData.catches || ''} onChange={e => setEditData({ ...editData, catches: e.target.value })} placeholder="e.g., 50" />
                  <Label>Run Outs (optional)</Label>
                  <Input type="number" value={editData.runOuts || ''} onChange={e => setEditData({ ...editData, runOuts: e.target.value })} placeholder="e.g., 10" />
                  <Label>Stumpings (optional)</Label>
                  <Input type="number" value={editData.stumpings || ''} onChange={e => setEditData({ ...editData, stumpings: e.target.value })} placeholder="e.g., 5" />
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        );
      
      case 'match': {
        // --- Setup for dropdown filtering and state ---
        const team1Players = getTeamPlayers(editData.team1Id || '');
        const team2Players = getTeamPlayers(editData.team2Id || '');
        const matchTypes = [
          { id: 't10', name: 'T10', overs: 10, balls: 6, innings: 2 },
          { id: 't10i', name: 'T10I', overs: 10, balls: 6, innings: 2 },
          { id: 't20', name: 'T20', overs: 20, balls: 6, innings: 2 },
          { id: 't20i', name: 'T20I', overs: 20, balls: 6, innings: 2 },
          { id: 'odi', name: 'ODI', overs: 50, balls: 6, innings: 2 },
          { id: 'test', name: 'Test', overs: 90, balls: 6, innings: 4 },
          { id: 'first-class', name: 'First-Class', overs: 90, balls: 6, innings: 4 },
          { id: 'custom', name: 'Custom', overs: 20, balls: 6, innings: 2 }
        ];
        // Ensure matchType is always lowercase for consistency
        const matchType = (editData.matchType || 't20').toLowerCase();
        // Vice-captain fields
        const team1CaptainId = editData.team1CaptainId || '';
        const team2CaptainId = editData.team2CaptainId || '';
        const team1ViceCaptainId = editData.team1ViceCaptainId || '';
        const team2ViceCaptainId = editData.team2ViceCaptainId || '';
        // Filtering logic
        const team1CaptainOptions = team1Players.filter(p => !team1ViceCaptainId || p.id !== team1ViceCaptainId);
        const team1ViceCaptainOptions = team1Players.filter(p => !team1CaptainId || p.id !== team1CaptainId);
        const team2CaptainOptions = team2Players.filter(p => !team2ViceCaptainId || p.id !== team2ViceCaptainId);
        const team2ViceCaptainOptions = team2Players.filter(p => !team2CaptainId || p.id !== team2CaptainId);
        
        return (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>Tournament</Label>
              <Select value={editData.tournamentId || ''} onValueChange={value => setEditData({ ...editData, tournamentId: value })}>
                <SelectTrigger><SelectValue placeholder="Select a tournament" /></SelectTrigger>
                <SelectContent>
                  {tournaments.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Team 1</Label>
                <Select value={editData.team1Id || ''} onValueChange={value => setEditData({ ...editData, team1Id: value, team1CaptainId: '', team1ViceCaptainId: '' })}>
                  <SelectTrigger><SelectValue placeholder="Select team" /></SelectTrigger>
                  <SelectContent>
                    {teams.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name} {t.shortCode ? `(${t.shortCode})` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Team 2</Label>
                <Select value={editData.team2Id || ''} onValueChange={value => setEditData({ ...editData, team2Id: value, team2CaptainId: '', team2ViceCaptainId: '' })} disabled={!editData.team1Id}>
                  <SelectTrigger><SelectValue placeholder="Select team" /></SelectTrigger>
                  <SelectContent>
                    {teams.filter(t => t.id !== editData.team1Id).map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name} {t.shortCode ? `(${t.shortCode})` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Team 1 Captain</Label>
                <Select value={team1CaptainId} onValueChange={value => setEditData({ ...editData, team1CaptainId: value })}>
                  <SelectTrigger><SelectValue placeholder="Select team captain" /></SelectTrigger>
                  <SelectContent>
                    {team1CaptainOptions.map(player => (
                      <SelectItem key={player.id} value={player.id}>{player.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Team 2 Captain</Label>
                <Select value={team2CaptainId} onValueChange={value => setEditData({ ...editData, team2CaptainId: value })}>
                  <SelectTrigger><SelectValue placeholder="Select team captain" /></SelectTrigger>
                  <SelectContent>
                    {team2CaptainOptions.map(player => (
                      <SelectItem key={player.id} value={player.id}>{player.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Team 1 Vice Captain</Label>
                <Select value={team1ViceCaptainId} onValueChange={value => setEditData({ ...editData, team1ViceCaptainId: value })}>
                  <SelectTrigger><SelectValue placeholder="Select team vice-captain" /></SelectTrigger>
                  <SelectContent>
                    {team1ViceCaptainOptions.map(player => (
                      <SelectItem key={player.id} value={player.id}>{player.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Team 2 Vice Captain</Label>
                <Select value={team2ViceCaptainId} onValueChange={value => setEditData({ ...editData, team2ViceCaptainId: value })}>
                  <SelectTrigger><SelectValue placeholder="Select team vice-captain" /></SelectTrigger>
                  <SelectContent>
                    {team2ViceCaptainOptions.map(player => (
                      <SelectItem key={player.id} value={player.id}>{player.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Match Type</Label>
              <Select value={matchType} onValueChange={value => {
                setEditData({ ...editData, matchType: value });
                if (value !== 'custom') {
                  const selectedType = matchTypes.find(type => type.id === value);
                  if (selectedType) {
                    setEditData(ed => ({
                      ...ed,
                      totalOvers: selectedType.overs,
                      ballsPerOver: selectedType.balls,
                      totalInnings: selectedType.innings,
                    }));
                  }
                }
              }} required>
                <SelectTrigger><SelectValue placeholder="Select match type" /></SelectTrigger>
                <SelectContent>
                  {matchTypes.map(type => (
                    <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {matchType === 'custom' && (
                <div className="mt-2">
                  <Input placeholder="Enter custom match type" value={editData.customMatchType || ''} onChange={e => setEditData({ ...editData, customMatchType: e.target.value })} className="mb-2" />
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Total Overs</Label>
                <Input id="totalOvers" type="number" min={1} value={editData.totalOvers || 20} onChange={e => setEditData({ ...editData, totalOvers: Number(e.target.value) })} disabled={matchType !== 'custom'} />
              </div>
              <div className="space-y-2">
                <Label>Balls per Over</Label>
                <Input id="ballsPerOver" type="number" min={1} max={8} value={editData.ballsPerOver || 6} onChange={e => setEditData({ ...editData, ballsPerOver: Number(e.target.value) })} disabled={matchType !== 'custom'} />
              </div>
              <div className="space-y-2">
                <Label>Total Wickets</Label>
                <Input id="totalWickets" type="number" value={editData.totalWickets || 10} onChange={e => setEditData({ ...editData, totalWickets: parseInt(e.target.value) })} min="1" required />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="totalInnings">Total Innings</Label>
              <Input id="totalInnings" type="number" value={editData.totalInnings || 2} onChange={e => setEditData({ ...editData, totalInnings: parseInt(e.target.value) })} min="1" max="4" disabled={matchType !== 'custom'} required />
              <p className="text-sm text-muted-foreground">
                {matchType === 'test' || matchType === 'first-class' ? '4 innings (2 per team)' : matchType === 'custom' ? 'Custom innings count' : '2 innings (1 per team)'}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="date">Match Date & Time</Label>
              <Input id="date" type="datetime-local" value={editData.date?.slice(0, 16) || ''} onChange={e => setEditData({ ...editData, date: e.target.value })} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input id="city" value={editData.city || ''} onChange={e => setEditData({ ...editData, city: e.target.value })} placeholder="Enter match city" required />
            </div>
          </div>
        );
      }
      
      default:
        return null;
    }
  };

  if (!showDialog) {
    return (
      <form onSubmit={e => { e.preventDefault(); handleEdit(); }}>
        {renderEditForm()}
      </form>
    );
  }

  return (
    <>
      {triggerId && (
        <button
          id={triggerId}
          style={{ display: 'none' }}
          type="button"
          onClick={() => setIsEditOpen(true)}
        />
      )}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogTrigger asChild>
          <Button variant="ghost" size="icon" className="p-1">
            <Edit className="w-4 h-4" />
          </Button>
        </DialogTrigger>
        <DialogContent className="max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit {entityType.charAt(0).toUpperCase() + entityType.slice(1)}</DialogTitle>
          </DialogHeader>
          {renderEditForm()}
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
            <Button onClick={handleEdit}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-600 hover:text-red-700">
            <Trash2 className="h-4 w-4" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Delete</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this {entityType}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
